import { isPhoneEmpty } from "Utils/PhoneUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import { Alert, AlertTitle, Box, Stack } from "@mui/material";
import {
  type SendMerchantAccessError,
  useSendMerchantAccessMutation,
} from "Api/Mutations/Merchants/useSendMerchantAccessMutation";
import { useHunterPrerequisitesQuery } from "Api/Queries/Hunter/useHunterPrerequisitesQuery";
import { useIdentityLookupQuery } from "Auth/Api/Queries/IdentityLookup/useIdentityLookupQuery";
import { useIsPricingSetForAllMerchantProducts } from "Components/Hunter/Merchants/CompleteRegistration/hooks/useIsPricingSetForAllMerchantProducts";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { hasApiProblemError } from "Infrastructure/Api/ApiUtils";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { useMerchantFacilityInfo } from "Components/Shared/Merchant/Facilities/hooks/useMerchantFacilityInfo";
import { hasFacilityPhotos } from "Components/Shared/Merchant/Facilities/utils";
import { useDebounce } from "Hooks/useDebounce";
import { type PartyDto, PartyType, RelationType } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Resources, useResource } from "Translations/Resources";
import { TodoList } from "Components/Shared/TodoList/TodoList";

const COMPONENT_RESOURCES =
  Resources.Hunter.Merchants.CompleteRegistration.SendAccess;

const emailSchema = z.email();
const formSchema = z.object({ email: emailSchema });

type FormData = z.infer<typeof formSchema>;

type Props = {
  merchantPublicID: string;
  prefillEmail?: string;
  partyData?: PartyDto;
};

export const SendAccessForm = ({
  merchantPublicID,
  prefillEmail,
  partyData,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { email: prefillEmail },
    resolver: zodResolver(formSchema),
  });

  const { mutate, isPending } = useSendMerchantAccessMutation({
    onError: (error: SendMerchantAccessError) => {
      if (error.data?.errors?.CompanyNumber) {
        setError("root", {
          message: t(COMPONENT_RESOURCES.CompanyNumberAlreadyRegistered, {
            companyNumber: partyData?.companyNumber,
          }),
        });
        return;
      }
      // 403 with BackOfficeTarget: the e-mail is an internal (Admin, AML, ...) account, which can
      // never take a merchant role — must not be mistaken for "merchant already exists" below.
      if (
        error.status === 403 &&
        hasApiProblemError(error, "BackOfficeTarget")
      ) {
        setError("email", {
          message: t(COMPONENT_RESOURCES.BackOfficeTarget),
        });
        return;
      }
      if (error.status === 409 || error.status === 403) {
        //409 Conflict, 403 Forbidden because user exists, but isn't merchant
        setError("email", {
          message: t(COMPONENT_RESOURCES.MerchantAlreadyExists),
        });
      }
    },
    onSuccess: () => {
      navigate(appUrl("merchants"));
    },
  });

  const { data: prerequisites, isLoading: isPrerequisitesLoading } =
    useHunterPrerequisitesQuery();

  const { isPricingSetForAllMerchantProducts } =
    useIsPricingSetForAllMerchantProducts(merchantPublicID);

  const { isRequired: requiresFacility } =
    useMerchantFacilityInfo(merchantPublicID);
  const { data: merchantData } = useGetMerchantQuery(merchantPublicID);

  const hasCompletedAllPrerequisites =
    !!prerequisites &&
    prerequisites.completedCount === prerequisites.totalCount;

  const isFirstMerchantRegistration =
    !!prerequisites && !prerequisites.hasUsedFreeMerchantRegistration;

  const isMerchantLegalEntity = partyData?.type === PartyType.LegalEntity;

  const mandatoryTodoItems = [
    ...(!isPrerequisitesLoading && !isFirstMerchantRegistration
      ? [
          {
            text: t(COMPONENT_RESOURCES.Todos.HunterPrerequisites),
            isCompleted: hasCompletedAllPrerequisites,
          },
        ]
      : []),
    ...(isMerchantLegalEntity
      ? [
          {
            text: t(COMPONENT_RESOURCES.Todos.ContactPerson),
            isCompleted: !!partyData?.relatedParties.some(
              ({ relationType }) => relationType === RelationType.ContactPerson,
            ),
          },
        ]
      : [
          {
            text: t(COMPONENT_RESOURCES.Todos.BasicInfo),
            isCompleted: !!partyData?.email && !isPhoneEmpty(partyData.phone),
          },
        ]),
    ...(requiresFacility
      ? [
          {
            text: t(COMPONENT_RESOURCES.Todos.Facilities),
            isCompleted:
              (merchantData?.facilities.length ?? 0) > 0 &&
              (merchantData?.facilities.every(hasFacilityPhotos) ?? false),
          },
        ]
      : []),
    {
      text: t(COMPONENT_RESOURCES.Todos.Pricing),
      isCompleted: isPricingSetForAllMerchantProducts,
    },
  ];

  const isEverythingMandatoryDone = mandatoryTodoItems.every(
    ({ isCompleted }) => isCompleted,
  );
  const emailValue = watch("email") ?? "";

  // Debounced IdentityLookup — only fires for syntactically-valid emails to keep the
  // authenticated rate-limited endpoint quiet while the Hunter is still typing.
  const debouncedEmail = useDebounce(emailValue, 400);
  const isEmailValid = emailSchema.safeParse(debouncedEmail).success;
  const { data: identityLookup } = useIdentityLookupQuery({
    login: isEmailValid ? debouncedEmail : null,
    companyPartyPublicID: partyData?.publicID ?? null,
    enabled: isEmailValid,
  });

  const hasMerchantRoleForCompany =
    identityLookup?.hasMerchantRoleForParty === true;
  const isBackOfficeIdentity = identityLookup?.isBackOffice === true;
  const isExistingIdentity =
    identityLookup?.exists === true &&
    !hasMerchantRoleForCompany &&
    !isBackOfficeIdentity;
  const isNewIdentity = identityLookup?.exists === false && isEmailValid;

  const isDisabled =
    !isEverythingMandatoryDone ||
    emailValue === "" ||
    hasMerchantRoleForCompany ||
    isBackOfficeIdentity;

  const onSubmit = ({ email }: FormData) => {
    mutate({ data: { login: email }, merchantPublicID });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection legend={t(COMPONENT_RESOURCES.Title)}>
        {isFirstMerchantRegistration && !hasCompletedAllPrerequisites && (
          <Box mb={2}>
            <Alert severity="info" sx={{ fontSize: "0.813rem" }}>
              <AlertTitle>
                {t(COMPONENT_RESOURCES.TestMerchantBannerTitle)}
              </AlertTitle>
              {t(COMPONENT_RESOURCES.TestMerchantBanner)}
            </Alert>
          </Box>
        )}
        <TodoList
          title={t(COMPONENT_RESOURCES.MandatoryStepsTitle)}
          todoItems={mandatoryTodoItems}
        />
      </FormSection>
      <Stack mb={4} gap={2}>
        <FormInput
          name="email"
          label={t(COMPONENT_RESOURCES.Login)}
          type="email"
          valueTransformer={(v) => v.toLowerCase()}
          errors={errors}
          control={control}
          helperText={
            errors.email?.message || t(COMPONENT_RESOURCES.LoginDescription)
          }
        />
        {hasMerchantRoleForCompany && (
          <Alert severity="error">
            {t(COMPONENT_RESOURCES.MerchantAlreadyHasAccessToThisCompany)}
          </Alert>
        )}
        {isBackOfficeIdentity && (
          <Alert severity="error">
            {t(COMPONENT_RESOURCES.BackOfficeTarget)}
          </Alert>
        )}
        {isExistingIdentity && (
          <Alert severity="info">
            {t(COMPONENT_RESOURCES.ExistingIdentityHint)}
          </Alert>
        )}
        {isNewIdentity && (
          <Alert severity="info">
            {t(COMPONENT_RESOURCES.NewIdentityHint)}
          </Alert>
        )}
        {errors.root?.message && (
          <Alert severity="error">{errors.root.message}</Alert>
        )}
      </Stack>
      <PrimaryButton
        fullWidth
        type="submit"
        disabled={isDisabled}
        isLoading={isPending}
        variant="contained"
        color="primary"
        endIcon={<ForwardToInboxOutlinedIcon />}
      >
        {t(COMPONENT_RESOURCES.SendAccessButton)}
      </PrimaryButton>
    </form>
  );
};
